// Turns an axios failure into something a Job Poster can act on.
//
// The API answers a rejected save with a bare string body: either an
// ActiveRecord::RecordInvalid message ("Validation failed: Shifts start time
// conflicts with another confirmed shift for Daniel Silas") or a short code
// ("overlapping_shift"). axios hides that in `error.response.data` and puts
// "Request failed with status code 422" on `error.message`, so every flash that
// read `error.message` told the EO nothing about what actually went wrong.

const VALIDATION_PREFIX = /^validation failed:\s*/i;
const NESTED_ATTRIBUTE_PREFIX = /^shifts\s+/i;

// GO4-1808 — Shift#confirmed_ats_free_at_new_time refuses a time change that
// would drop a confirmed AT on top of another commitment. Capture who is
// blocking it so we can name them.
const CONFIRMED_CONFLICT = /conflicts with another confirmed shift for\s+(.+?)[.\s]*$/i;

const CODE_MESSAGES = {
  overlapping_shift: 'This shift overlaps another shift that is already confirmed.',
  stripe_account_required: 'Add a payment method before confirming this shift.',
};

const isPlural = (names) => /,|\sand\s/.test(names);

export const serverErrorBody = (error) => {
  const data = error?.response?.data;

  if (typeof data === 'string') return data.trim();

  return (data?.message || data?.error || '').toString().trim();
};

export const doubleBookedMessage = (names) =>
  `${names} ${isPlural(names) ? 'are' : 'is'} already confirmed for another shift in that time ` +
  'window. Pick a time that does not overlap, or remove them from this shift first.';

// Returns the reason the server gave, rewritten for a Job Poster, or `fallback`
// when the response carried nothing usable.
export const serverErrorMessage = (error, fallback = '') => {
  const body = serverErrorBody(error);
  if (!body) return fallback;

  if (CODE_MESSAGES[body]) return CODE_MESSAGES[body];

  const conflict = body.match(CONFIRMED_CONFLICT);
  if (conflict) return doubleBookedMessage(conflict[1]);

  return body.replace(VALIDATION_PREFIX, '').replace(NESTED_ATTRIBUTE_PREFIX, '') || fallback;
};

// True when the save was refused because an athletic trainer on this shift is
// already committed elsewhere. The EO has to make a decision about it, so this
// one belongs in a modal rather than a flash that self-dismisses after ten
// seconds.
export const isSchedulingConflict = (error) => {
  const body = serverErrorBody(error);

  return body === 'overlapping_shift' || CONFIRMED_CONFLICT.test(body);
};

// Just the names, so a modal can give them a line of their own.
export const conflictingTrainers = (error) => {
  const match = serverErrorBody(error).match(CONFIRMED_CONFLICT);

  return match ? match[1] : '';
};
